import { supabase } from "@/integrations/supabase/client";
import { handleServiceError } from "../utils/errorHandling";
import { 
  SignUpData, 
  SignInData, 
  SignInResponse, 
  SignUpResponse, 
  AuthErrorResponse 
} from "@/types/auth";
import { familyInviteService } from "@/services/family";
import { toast } from "sonner";
import { analytics } from "@/lib/analytics";
import { LEGAL_VERSIONS, LegalAgreementAcceptance } from "@/constants/legalVersions";
import { getClientInfo } from "@/utils/clientInfo";

/**
 * Service for authentication mutation operations (sign up, sign in, sign out)
 */
export const authMutationService = {
  /**
   * Sign up a new user - optimized for speed
   */
  signUp: async (data: SignUpData): Promise<SignUpResponse> => {
    try {
      // Get client info synchronously (no more async IP lookup)
      const clientInfo = getClientInfo();
      
      // Prepare legal agreement acceptance data
      const legalAgreementAcceptance: LegalAgreementAcceptance = {
        timestamp: new Date().toISOString(),
        terms_version: LEGAL_VERSIONS.TERMS_OF_SERVICE,
        privacy_version: LEGAL_VERSIONS.PRIVACY_POLICY,
        ip_address: clientInfo.ipAddress,
        user_agent: clientInfo.userAgent,
      };
      
      const { email, password, ...metaData } = data;
      
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.first_name,
            last_name: data.last_name || 'User',
            legal_agreements_accepted: legalAgreementAcceptance,
          },
        },
      });

      if (error) throw error;
      
      // New validation to ensure a user was actually created
      if (!authData?.user) {
        throw new Error("User registration failed. Please try again.");
      }
      
      // Send welcome email in background - don't block signup success
      setTimeout(async () => {
        try {
          console.log('Sending welcome email in background...');
          const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
            body: {
              firstName: data.first_name,
              lastName: data.last_name || 'User',
              email: data.email,
            },
          });
          
          if (emailError) {
            console.error('Welcome email failed:', emailError);
          } else {
            console.log('Welcome email sent successfully');
          }
        } catch (emailError) {
          console.error('Welcome email error:', emailError);
        }
      }, 0);
      
      // Track successful signup
      analytics.setUser(authData.user.id);
      analytics.track('user_signed_up', {
        first_name: data.first_name,
        last_name: data.last_name || 'User',
        has_invite_code: !!data.invite_code,
        legal_agreements_accepted: true,
        terms_version: LEGAL_VERSIONS.TERMS_OF_SERVICE,
        privacy_version: LEGAL_VERSIONS.PRIVACY_POLICY,
      });
      
      analytics.identify({
        first_name: data.first_name,
        last_name: data.last_name || 'User',
        email: data.email,
      });
      
      // If invite code was provided, join family after registration
      if (data.invite_code && authData.user) {
        try {
          const result = await familyInviteService.joinFamilyByInviteCode(authData.user.id, data.invite_code);
          
          if (result.success) {
            analytics.track('family_joined_during_signup', {
              invite_code: data.invite_code,
              family_id: result.familyId,
            });
            toast.success("Successfully registered and joined family!");
          } else {
            toast.error(`Registration successful, but failed to join family: ${result.message}`);
          }
        } catch (joinError) {
          console.error("Error joining family:", joinError);
          analytics.trackError(joinError as Error, 'family_join_during_signup');
          toast.error("Registration successful, but failed to join family. You can join later using the invite code.");
        }
      }
      
      return { user: authData.user, error: null };
    } catch (error) {
      console.error("Signup error details:", error);
      
      analytics.trackAuthError('signup', error instanceof Error ? error.message : 'Unknown error');
      handleServiceError(error, "Failed to sign up");
      return { user: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },

  signIn: async (data: SignInData): Promise<SignInResponse> => {
    try {
      const { email, password } = data;
      
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) throw error;
      
      // Validate that user has a profile and is active after successful authentication
      if (authData.user) {
        try {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id, is_active')
            .eq('id', authData.user.id)
            .single();
          
          if (profileError || !profile) {
            console.error('Profile validation failed:', profileError);
            
            // Sign out the user immediately
            await supabase.auth.signOut();
            
            return { 
              session: null, 
              error: new Error('Your account is missing required profile information. Please contact support or try registering again.') 
            };
          }
          
          // Check if profile is active
          if (!profile.is_active) {
            console.log('User account is deactivated, blocking login');
            
            // Sign out the user immediately
            await supabase.auth.signOut();
            
            return { 
              session: null, 
              error: new Error('Your account has been deactivated. Please contact support if you believe this is an error.') 
            };
          }
        } catch (profileCheckError) {
          console.error('Error checking profile:', profileCheckError);
          
          // Sign out the user immediately
          await supabase.auth.signOut();
          
          return { 
            session: null, 
            error: new Error('Unable to verify account information. Please try again or contact support.') 
          };
        }
      }
      
      // Track successful signin
      if (authData.user) {
        analytics.setUser(authData.user.id);
        analytics.track('user_signed_in', {
          login_method: 'password',
        });
      }
      
      return { session: authData.session, error: null };
    } catch (error) {
      analytics.trackAuthError('signin', error instanceof Error ? error.message : 'Unknown error');
      handleServiceError(error, "Failed to sign in");
      return { session: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },

  signOut: async (): Promise<AuthErrorResponse> => {
    console.log('Starting simple sign-out...');
    
    try {
      // Track signout before clearing analytics
      analytics.track('user_signed_out');
      
      // Simple sign out - let Supabase handle everything
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign-out error:', error);
        throw error;
      }
      
      console.log('Sign-out successful');
      return { error: null };
    } catch (error) {
      console.error('Sign-out failed:', error);
      analytics.trackAuthError('signout', error instanceof Error ? error.message : 'Unknown error');
      return { error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
};
